import { useFrame, useThree } from "@react-three/fiber";
import { useContext, useEffect, useLayoutEffect, useMemo } from "react";
import { useDragStore } from "../stores/dragStore";
import { ProductStoreContext } from "./ProductStoreContext";
import { createPostProcessingCameraActivity } from "./postProcessingCameraActivity";
import {
  type ProductViewerRenderQuality,
  productViewerDpr,
} from "./productViewerRenderQuality";

/**
 * Changes only the live canvas, never a shared stage or capture. Resizing is
 * performed before post-processing's priority-1 callback, so every attachment
 * observes the new drawing buffer on the very same frame.
 */
export function ProductViewerAdaptiveResolution({
  quality,
}: {
  quality: ProductViewerRenderQuality;
}) {
  const camera = useThree((state) => state.camera);
  const get = useThree((state) => state.get);
  const invalidate = useThree((state) => state.invalidate);
  // Absent outside a product viewer; the camera invalidates on every change.
  const store = useContext(ProductStoreContext);
  const activity = useMemo(
    () => createPostProcessingCameraActivity(camera, invalidate),
    [camera, invalidate],
  );
  const deviceDpr = () =>
    globalThis.window === undefined
      ? get().viewport.initialDpr
      : globalThis.devicePixelRatio;

  // biome-ignore lint/correctness/useExhaustiveDependencies: a quality change needs a frame, not activity/DPR cleanup.
  useLayoutEffect(() => {
    invalidate();
  }, [invalidate, quality]);

  useLayoutEffect(() => {
    return () => {
      activity.reset();
      const state = get();
      const restored = productViewerDpr({
        width: state.size.width,
        height: state.size.height,
        deviceDpr:
          globalThis.window === undefined
            ? state.viewport.initialDpr
            : globalThis.devicePixelRatio,
        interacting: false,
      });
      if (state.viewport.dpr !== restored) state.setDpr(restored);
    };
  }, [activity, get]);

  useEffect(
    () =>
      useDragStore.subscribe((state, previous) => {
        if (state.isDragActive !== previous.isDragActive) invalidate();
      }),
    [invalidate],
  );

  useEffect(() => {
    if (globalThis.window === undefined || !globalThis.matchMedia) return;
    let media: MediaQueryList;
    const changed = () => {
      media?.removeEventListener("change", changed);
      media = globalThis.matchMedia(
        `(resolution: ${globalThis.devicePixelRatio}dppx)`,
      );
      media.addEventListener("change", changed);
      invalidate();
    };
    changed();
    return () => media.removeEventListener("change", changed);
  }, [invalidate]);

  useFrame(() => {
    const state = get();
    // Sample motion even during a part drag so releasing it cannot strand a
    // pending camera transition at the interaction ratio.
    const cameraMoved = activity.update(
      store?.getState().isUserControllingCamera ?? false,
    );
    const dpr = productViewerDpr({
      width: state.size.width,
      height: state.size.height,
      deviceDpr: deviceDpr(),
      interacting: cameraMoved || useDragStore.getState().isDragActive,
      quality,
    });
    if (Math.abs(state.viewport.dpr - dpr) > 1e-6) state.setDpr(dpr);
  });

  return null;
}
